import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getOidcPostLogoutRedirectUri } from '@/lib/oidc';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { fetchUserIdentity } from '@/modules/user/client-user';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
    /** Resolved app UUID (profiles.user_id) — use this for all API calls and routing */
    id: string;
    /** Original Zitadel numeric sub from the OIDC token */
    sub: string;
    email: string | undefined;
    user_metadata: {
        display_name?: string;
        username?: string;
        full_name?: string;
    };
    app_metadata: Record<string, unknown>;
    aud: string;
}

interface AuthContextType {
    user: AuthUser | null;
    roles: string[];
    loading: boolean;
    isAuthenticated: boolean;
    signIn: (redirectTo?: string) => Promise<void>;
    signOut: () => Promise<void>;
    /** @deprecated Use signIn() — redirects to Zitadel */
    signUp: (...args: any[]) => Promise<{ data: null; error: Error }>;
    /** @deprecated Use signIn() — redirects to Zitadel */
    signInWithGithub: () => Promise<{ error: null }>;
    /** @deprecated Use signIn() — redirects to Zitadel */
    signInWithGoogle: () => Promise<{ error: null }>;
    /** @deprecated Reset password via Zitadel portal */
    resetPassword: (...args: any[]) => Promise<{ error: Error }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ─── Provider ─────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const oidcAuth = useOidcAuth();
    const [roles, setRoles] = useState<string[]>([]);
    /** App UUID from /api/me/identity — never the numeric Zitadel sub */
    const [resolvedId, setResolvedId] = useState<string | null>(null);
    /** False until identity fetch finished (success or failure) for the current session */
    const [identityReady, setIdentityReady] = useState(false);
    /** Sub we already resolved — skips refetch on OIDC silent-renew isLoading toggles */
    const loadedForSubRef = useRef<string | null>(null);
    const fetchGenRef = useRef(0);

    useEffect(() => {
        if (oidcAuth.isLoading) return;

        if (!oidcAuth.isAuthenticated || !oidcAuth.user) {
            fetchGenRef.current += 1;
            loadedForSubRef.current = null;
            setRoles([]);
            setResolvedId(null);
            setIdentityReady(true);
            return;
        }

        const sub = oidcAuth.user.profile.sub ?? '';

        // Already loaded for this OIDC subject — do not refetch (or clear roles) on renew
        if (loadedForSubRef.current === sub) {
            setIdentityReady(true);
            return;
        }

        const gen = ++fetchGenRef.current;
        setIdentityReady(false);

        fetchUserIdentity()
            .then(({ id, roles: r }) => {
                if (gen !== fetchGenRef.current) return;
                if (!id || !UUID_RE.test(id)) {
                    console.error('[AuthProvider] /api/me/identity returned non-UUID id:', id);
                    setIdentityReady(true);
                    return;
                }
                loadedForSubRef.current = sub;
                setResolvedId(id);
                setRoles(Array.isArray(r) ? r : []);
                setIdentityReady(true);
            })
            .catch(err => {
                if (gen !== fetchGenRef.current) return;
                console.error('[AuthProvider] Failed to fetch identity:', err);
                // Never wipe roles/id on failure — allow retry when auth state settles again
                setIdentityReady(true);
            });
    }, [oidcAuth.isAuthenticated, oidcAuth.isLoading]);

    const signedIn = oidcAuth.isAuthenticated && !!oidcAuth.user;
    const oidcUser = signedIn ? oidcAuth.user : null;
    const oidcSub = oidcUser?.profile.sub ?? '';

    // Chrome can render from OIDC; API callers should wait until `id` is a real UUID.
    const user: AuthUser | null = oidcUser
        ? {
              id: resolvedId ?? '',
              sub: oidcSub,
              email: oidcUser.profile.email,
              user_metadata: {
                  display_name: oidcUser.profile.name,
                  full_name: oidcUser.profile.name,
                  username: oidcUser.profile.preferred_username,
              },
              app_metadata: {},
              aud: String(oidcUser.profile.aud ?? ''),
          }
        : null;

    return (
        <AuthContext.Provider
            value={{
                user,
                roles,
                loading: oidcAuth.isLoading || (oidcAuth.isAuthenticated && !identityReady),
                isAuthenticated: oidcAuth.isAuthenticated,
                signIn: (redirectTo = '/') => oidcAuth.signinRedirect({ state: { redirectTo } }),
                signOut: () => oidcAuth.signoutRedirect({ post_logout_redirect_uri: getOidcPostLogoutRedirectUri() }),
                signUp: async () => ({
                    data: null,
                    error: new Error('Sign up via Zitadel — use the /authz page'),
                }),
                signInWithGithub: async () => { await oidcAuth.signinRedirect(); return { error: null }; },
                signInWithGoogle: async () => { await oidcAuth.signinRedirect(); return { error: null }; },
                resetPassword: async () => ({
                    error: new Error('Reset password via the Zitadel portal'),
                }),
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
